/* eslint-disable no-nested-ternary */
import React, { useMemo } from 'react'
import {
	Spin,
	Form,
	Input,
	Row,
	Col,
	Select,
	FormInstance,
	Upload,
	Space,
	Tooltip,
	Button,
} from 'antd'
import {
	RiCheckboxCircleLine,
	RiExternalLinkLine,
	RiInformationLine,
	RiUpload2Line,
} from 'react-icons/ri'

import getFileNameFromURL from 'utils/getFileNameFromURL'
import { TemplateCallAction } from 'types'
import useSingleBroadcast from 'hooks/useSingleBroadcast'
import { BroadcastFormValues } from 'component/Broadcast/BroadcastDetails'

import styles from './styles.module.scss'

const { Item, List } = Form
const { TextArea } = Input

const typeOptions = [
	{ label: 'Contact Field', value: 'CONTACT' },
	{ label: 'Fixed Value', value: 'FIXED' },
	{ label: 'Unique Value', value: 'UNIQUE' },
]

interface Props {
	form: FormInstance<BroadcastFormValues>
	canEditBroadcast: boolean
}

const BroadcastFormParameters: React.FC<Props> = ({
	form,
	canEditBroadcast,
}) => {
	const { broadcast } = useSingleBroadcast()

	const template = useMemo(
		() =>
			typeof broadcast?.template !== 'string' &&
			broadcast?.template !== null &&
			broadcast?.template !== undefined
				? broadcast.template
				: null,
		[broadcast?.template]
	)

	const sampleMedia = useMemo(() => {
		if (!template) {
			return null
		}

		const sampleAttachment =
			template.type === 'DOCUMENT'
				? template.document
				: template.type === 'IMAGE'
				? template.image
				: template.type === 'VIDEO'
				? template.video
				: null

		if (!sampleAttachment || !sampleAttachment.link) {
			return null
		}

		return {
			fileName:
				sampleAttachment?.fileName || getFileNameFromURL(sampleAttachment.link),
			url: sampleAttachment.link,
			size: 0,
		}
	}, [template])

	const buttonTemplateURL = (() => {
		if (template?.action?.type !== 'CALL_TO_ACTION') return ''

		const items = (template?.action?.items as TemplateCallAction[]) ?? []

		const url =
			items.find(
				(item: TemplateCallAction) =>
					item.type === 'URL' && item.url_type === 'DYNAMIC'
			)?.button_value || ''

		return url
	})()

	return (
		<Spin spinning={template === null} tip='Loading Template'>
			<Row gutter={16}>
				<Col span={8}>
					<Item label='Template Preview *'>
						<TextArea value={template?.formattedTemplate || ''} disabled />
					</Item>

					<Item
						label='Header Preview'
						hidden={!((template?.header?.params.length || 0) > 0)}
					>
						<Input value={template?.header?.text} disabled />
					</Item>

					<Item label='Dynamic URL' hidden={!buttonTemplateURL}>
						<Input value={buttonTemplateURL} disabled />
					</Item>
				</Col>
				<Col span={broadcast?.audienceType === 'IMPORTED_FILE' ? 14 : 7}>
					<div className={styles.csvInfo}>
						<div className={styles.csvTitleWrapper}>
							<span className={styles.csvTitle}>Imported CSV</span>
							{broadcast?.audienceType === 'IMPORTED_FILE' ? (
								<RiCheckboxCircleLine color='#389E0D' />
							) : (
								<RiInformationLine color='#F4CA17' />
							)}
						</div>
						{broadcast?.audienceType === 'IMPORTED_FILE' ? (
							<div className={styles.csvDetailWrapper}>
								<div className={styles.csvDetailRow}>
									<span className={styles.csvDetailLabel}>File Name:</span>
									<span className={styles.csvDetailValue}>
										{broadcast.contactFilter?.csv_file?.name || 'N/A'}
									</span>
								</div>
								<div className={styles.csvDetailRow}>
									<span className={styles.csvDetailLabel}>Status:</span>
									<span className={styles.csvDetailValue}>CSV is Mapped.</span>
								</div>
							</div>
						) : (
							<div className={styles.noCsvBox}>No CSV file imported</div>
						)}
					</div>
				</Col>
			</Row>

			<div
				hidden={(form.getFieldValue('templateHeaderParameter').length || 0) === 0}
			>
				<div className='custom-label'>Header Parameters *</div>
				<List name='templateHeaderParameter'>
					{fields =>
						fields.map(({ key, name }) => (
							<Row key={key} gutter={16}>
								<Col span={1}>
									<span className='parameter-number'>
										{`{{${form.getFieldValue([
											'templateHeaderParameter',
											key,
											'number',
										])}}}`}
									</span>
								</Col>
								<Col span={7}>
									<Item
										name={[name, 'type']}
										rules={[{ required: true, message: 'Select Type!' }]}
									>
										<Select
											placeholder='Select Type'
											options={[
												...typeOptions,
												...(broadcast?.audienceType === 'IMPORTED_FILE'
													? [{ label: 'From Imported CSV', value: 'CSV' }]
													: []),
											]}
											optionFilterProp='label'
											disabled={!canEditBroadcast}
										/>
									</Item>
								</Col>
								<Col
									span={7}
									hidden={
										form.getFieldValue(['templateHeaderParameter', key, 'type']) !==
										'CONTACT'
									}
								>
									<Item
										name={[name, 'contactField']}
										rules={[
											({ getFieldValue }) => ({
												validator(_, value) {
													if (
														getFieldValue(['templateHeaderParameter', key, 'type']) !==
														'CONTACT'
													) {
														return Promise.resolve()
													}
													if (value) {
														return Promise.resolve()
													}

													return Promise.reject(new Error('Please Select Contacts'))
												},
											}),
										]}
									>
										<Select
											placeholder='Select Contact Field'
											optionFilterProp='label'
											disabled={!canEditBroadcast}
										/>
									</Item>
								</Col>
								<Col
									span={7}
									hidden={
										form.getFieldValue(['templateHeaderParameter', key, 'type']) !== 'CSV'
									}
								>
									<Item
										name={[name, 'csvField']}
										rules={[
											({ getFieldValue }) => ({
												validator(_, value) {
													if (
														getFieldValue(['templateHeaderParameter', key, 'type']) !== 'CSV'
													) {
														return Promise.resolve()
													}
													if (value) {
														return Promise.resolve()
													}

													return Promise.reject(new Error('Please Select CSV Field'))
												},
											}),
										]}
									>
										<Select
											placeholder='Select CSV Field'
											optionFilterProp='label'
											disabled={!canEditBroadcast}
										/>
									</Item>
								</Col>
								<Col span={7}>
									<Item
										name={[name, 'value']}
										rules={[{ required: true, message: 'Enter Value' }]}
									>
										<Input
											placeholder={
												form.getFieldValue(['templateHeaderParameter', key, 'type']) ===
												'FIXED'
													? 'Enter Value'
													: 'Enter Fallback Value'
											}
											disabled={!canEditBroadcast}
										/>
									</Item>
								</Col>
							</Row>
						))
					}
				</List>
			</div>

			<div hidden={(form.getFieldValue('parameters').length || 0) === 0}>
				<div className='custom-label'>Parameters *</div>
				<List name='parameters'>
					{fields =>
						fields.map(({ key, name }) => (
							<Row key={key} gutter={16}>
								<Col span={1}>
									<span className='parameter-number'>
										{`{{${form.getFieldValue(['parameters', key, 'number'])}}}`}
									</span>
								</Col>
								<Col span={7}>
									<Item
										name={[name, 'type']}
										rules={[{ required: true, message: 'Select Type!' }]}
									>
										<Select
											placeholder='Select Type'
											options={[
												...typeOptions,
												...(broadcast?.audienceType === 'IMPORTED_FILE'
													? [{ label: 'From Imported CSV', value: 'CSV' }]
													: []),
											]}
											optionFilterProp='label'
											disabled={!canEditBroadcast}
										/>
									</Item>
								</Col>
								<Col
									span={7}
									hidden={form.getFieldValue(['parameters', key, 'type']) !== 'CONTACT'}
								>
									<Item
										name={[name, 'contactField']}
										rules={[
											({ getFieldValue }) => ({
												validator(_, value) {
													if (getFieldValue(['parameters', key, 'type']) !== 'CONTACT') {
														return Promise.resolve()
													}
													if (value) {
														return Promise.resolve()
													}

													return Promise.reject(new Error('Please Select Contacts'))
												},
											}),
										]}
									>
										<Select
											placeholder='Select Contact Field'
											optionFilterProp='label'
											disabled={!canEditBroadcast}
										/>
									</Item>
								</Col>
								<Col
									span={7}
									hidden={form.getFieldValue(['parameters', key, 'type']) !== 'CSV'}
								>
									<Item
										name={[name, 'csvField']}
										rules={[
											({ getFieldValue }) => ({
												validator(_, value) {
													if (getFieldValue(['parameters', key, 'type']) !== 'CSV') {
														return Promise.resolve()
													}
													if (value) {
														return Promise.resolve()
													}

													return Promise.reject(new Error('Please Select CSV Field'))
												},
											}),
										]}
									>
										<Select
											placeholder='Select CSV Field'
											optionFilterProp='label'
											disabled={!canEditBroadcast}
										/>
									</Item>
								</Col>
								<Col span={7}>
									<Item
										name={[name, 'value']}
										rules={[{ required: true, message: 'Enter Value' }]}
									>
										<Input
											placeholder={
												form.getFieldValue(['parameters', key, 'type']) === 'FIXED'
													? 'Enter Value'
													: 'Enter Fallback Value'
											}
											disabled={!canEditBroadcast}
										/>
									</Item>
								</Col>
							</Row>
						))
					}
				</List>
			</div>

			<div hidden={(form.getFieldValue('templateUrlParameter').length || 0) === 0}>
				<div className='custom-label'>URL Parameters *</div>
				<List name='templateUrlParameter'>
					{fields =>
						fields.map(({ key, name }) => (
							<Row key={key} gutter={16}>
								<Col span={1}>
									<span className='parameter-number'>
										{`{{${form.getFieldValue(['templateUrlParameter', key, 'number'])}}}`}
									</span>
								</Col>
								<Col span={7}>
									<Item
										name={[name, 'type']}
										rules={[{ required: true, message: 'Select Type!' }]}
									>
										<Select
											placeholder='Select Type'
											options={[
												...typeOptions,
												...(broadcast?.audienceType === 'IMPORTED_FILE'
													? [{ label: 'From Imported CSV', value: 'CSV' }]
													: []),
											]}
											optionFilterProp='label'
											disabled={!canEditBroadcast}
										/>
									</Item>
								</Col>
								<Col
									span={7}
									hidden={
										form.getFieldValue(['templateUrlParameter', key, 'type']) !==
										'CONTACT'
									}
								>
									<Item
										name={[name, 'contactField']}
										rules={[
											({ getFieldValue }) => ({
												validator(_, value) {
													if (
														getFieldValue(['templateUrlParameter', key, 'type']) !== 'CONTACT'
													) {
														return Promise.resolve()
													}
													if (value) {
														return Promise.resolve()
													}

													return Promise.reject(new Error('Please Select Contacts'))
												},
											}),
										]}
									>
										<Select
											placeholder='Select Contact Field'
											optionFilterProp='label'
											disabled={!canEditBroadcast}
										/>
									</Item>
								</Col>
								<Col
									span={7}
									hidden={
										form.getFieldValue(['templateUrlParameter', key, 'type']) !== 'CSV'
									}
								>
									<Item
										name={[name, 'csvField']}
										rules={[
											({ getFieldValue }) => ({
												validator(_, value) {
													if (
														getFieldValue(['templateUrlParameter', key, 'type']) !== 'CSV'
													) {
														return Promise.resolve()
													}
													if (value) {
														return Promise.resolve()
													}

													return Promise.reject(new Error('Please Select CSV Field'))
												},
											}),
										]}
									>
										<Select
											placeholder='Select CSV Field'
											optionFilterProp='label'
											disabled={!canEditBroadcast}
										/>
									</Item>
								</Col>
								<Col span={7}>
									<Item
										name={[name, 'value']}
										rules={[{ required: true, message: 'Enter Value' }]}
									>
										<Input
											placeholder={
												form.getFieldValue(['templateUrlParameter', key, 'type']) ===
												'FIXED'
													? 'Enter Value'
													: 'Enter Fallback Value'
											}
											disabled={!canEditBroadcast}
										/>
									</Item>
								</Col>
							</Row>
						))
					}
				</List>
			</div>

			<Row gutter={16}>
				<Col span={7}>
					<Item
						name='mediaSelectionType'
						label='Media *'
						hidden={template?.type === 'TEXT'}
						rules={[
							{
								required: template?.type !== 'TEXT',
								message: 'Please Select Attachment Type!',
							},
						]}
					>
						<Select
							placeholder='Select Attachment Type'
							options={[
								{ label: 'Same for all', value: 'STATIC' },
								{ label: 'Unique for all', value: 'DYNAMIC' },
							]}
							optionFilterProp='label'
							disabled={!canEditBroadcast}
						/>
					</Item>
					<Item
						label='Upload Attachment *'
						name='staticAttachment'
						hidden={
							template?.type === 'TEXT' ||
							form.getFieldValue('mediaSelectionType') !== 'STATIC'
						}
						rules={[
							{
								required:
									template?.type !== 'TEXT' &&
									form.getFieldValue('mediaSelectionType') === 'STATIC',
								message: 'Please Upload Attachment',
							},
						]}
					>
						<Upload
							maxCount={1}
							multiple={false}
							accept={
								template?.type === 'IMAGE'
									? 'image/jpeg, image/png'
									: template?.type === 'VIDEO'
									? 'video/mp4, video/3gpp'
									: template?.type === 'DOCUMENT'
									? 'application/pdf'
									: '*'
							}
							disabled={!canEditBroadcast}
						>
							<Button type='ghost' icon={<RiUpload2Line />} className='upload-button'>
								Click to Upload
							</Button>
							<div>
								<span>Image: 5MB, Video: 16MB, Document: 100MB</span>
							</div>
						</Upload>
					</Item>
					<Item
						hidden={
							sampleMedia === null ||
							form.getFieldValue('mediaSelectionType') !== 'STATIC'
						}
					>
						<Space align='start' size='middle'>
							<div className='custom-label'>
								<Space>
									<span>Use Sample Media</span>
									<Tooltip title='Preview Sample'>
										<a target='blank' href={sampleMedia?.url}>
											<RiExternalLinkLine />
										</a>
									</Tooltip>
								</Space>
							</div>
						</Space>
					</Item>
				</Col>
			</Row>
		</Spin>
	)
}

export default BroadcastFormParameters
